import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CurrentUserService } from '@app/core/services';
import { LiveChatApi } from '@app/data/api';
import { LiveChatRealtimeService } from '@app/services/live-chat-realtime.service';
import { AppIcon } from '../../controls/app-icon/app-icon';
import { TooltipDirective } from '../../controls/app-tooltip/tooltip.directive';
import { ProfileMenuComponent } from '../profile-menu/profile-menu';

interface ModuleNavigationItem {
  readonly route: string;
  readonly icon: string;
  readonly labelKey: string;
  readonly label: () => string;
}

@Component({
  selector: 'app-nav-modules',
  imports: [AppIcon, RouterLink, RouterLinkActive, ProfileMenuComponent, TooltipDirective],
  templateUrl: './nav-modules.html',
  styleUrl: './nav-modules.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'handleDocumentClick($event)',
    '(document:keydown.escape)': 'closeProfileMenu()',
  },
})
export class NavModules implements OnInit {
  private static readonly LOCALIZATION_KEYS = [
    'nav.ariaLabel',
    'nav.dashboard',
    'nav.inbox',
    'nav.history',
    'nav.contacts',
    'nav.profile',
  ] as const;

  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly translateService = inject(TranslateService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly liveChatApi = inject(LiveChatApi);
  private readonly liveChatRealtime = inject(LiveChatRealtimeService);

  protected readonly isProfileMenuOpen = signal(false);
  protected readonly agentAvailable = this.liveChatRealtime.available;
  protected readonly labels = signal<Record<string, string>>({});
  protected readonly navAriaLabel = computed(() => this.label('nav.ariaLabel'));
  protected readonly openProfileMenuLabel = computed(() => this.label('nav.profile'));
  protected readonly navigationItems: readonly ModuleNavigationItem[] = [
    { route: '/dashboard', icon: 'fa-solid fa-house', labelKey: 'nav.dashboard', label: () => this.label('nav.dashboard') },
    { route: '/inbox', icon: 'fa-solid fa-inbox', labelKey: 'nav.inbox', label: () => this.label('nav.inbox') },
    { route: '/history', icon: 'fa-solid fa-clock-rotate-left', labelKey: 'nav.history', label: () => this.label('nav.history') },
    { route: '/contacts', icon: 'fa-solid fa-address-book', labelKey: 'nav.contacts', label: () => this.label('nav.contacts') },
  ];
  protected readonly userInitials = computed(() => {
    const appUser = this.currentUserService.currentUser()?.appUser;
    const firstInitial = appUser?.firstName?.trim().charAt(0) ?? '';
    const lastInitial = appUser?.lastName?.trim().charAt(0) ?? '';
    return `${firstInitial}${lastInitial}`.toUpperCase() || '-';
  });

  ngOnInit(): void {
    void this.loadAgentAvailability();
    this.translateService
      .stream([...NavModules.LOCALIZATION_KEYS])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translations) => this.labels.set(translations as Record<string, string>));
  }

  private async loadAgentAvailability(): Promise<void> {
    try {
      const [user, agents] = await Promise.all([
        this.currentUserService.getCurrentUser(),
        firstValueFrom(this.liveChatApi.getAssignableAgents()),
      ]);
      const appUserId = user?.appUser?.appUserId;
      this.liveChatRealtime.syncAvailability(agents.some((agent) => agent.appUser.appUserId === appUserId && agent.isOnline));
    } catch {
      this.liveChatRealtime.syncAvailability(false);
    }
  }

  protected toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isProfileMenuOpen.update((open) => !open);
  }

  protected closeProfileMenu(): void {
    this.isProfileMenuOpen.set(false);
  }

  protected isMobile(): boolean {
    return window.innerWidth <= 48 * 16;
  }

  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Node && !this.hostElement.nativeElement.contains(target)) {
      this.closeProfileMenu();
    }
  }

  private label(key: string): string {
    return this.labels()[key] ?? key;
  }
}
